import { hostBridge } from '@publicala/delfino';
import type { PlayTrackPayload, PlayerStateChangedPayload, SeekToPositionPayload } from '@publicala/delfino';
import { isE2EMockActive } from '../e2eMockGuard';

/**
 * Playback API for communicating with the Host.
 * Volpe calls these methods to notify the Host about playback events.
 */
class PlaybackAPI {
    /**
     * Notifies the Host that a track is playing.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     * @param payload - Contains trackIndex (optional)
     */
    async playTrack(payload: PlayTrackPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.playback.playTrack(payload);
    }

    /**
     * Notifies the Host that playback was paused.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async pauseTrack(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.playback.pauseTrack();
    }

    /**
     * Notifies the Host that moved to the next track.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async nextTrack(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.playback.nextTrack();
    }

    /**
     * Notifies the Host that moved to the previous track.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async previousTrack(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.playback.previousTrack();
    }

    /**
     * Notifies the Host about a seek to a specific position.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     * @param payload - Contains newSeekPositionInSeconds, duration, rate
     */
    async seekToPosition(payload: SeekToPositionPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.playback.seekToPosition(payload);
    }

    /**
     * Notifies the Host about player state changes.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     * @param payload - Contains the current player state
     */
    async playerStateChanged(payload: PlayerStateChangedPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.playback.playerStateChanged(payload);
    }
}

export const playbackAPI = new PlaybackAPI();
